// Wellenformen: Mini-Wellenform für Listen, Fortschritts-Wellenform des laufenden Titels und der
// Mehrspur-Übergangseditor (Segue im Sendeplan, Voicetracking). Eigene Gestaltung.
//
// Zeitachse des Editors: das erste Element beginnt bei t = 0 mit seinem Cue-In; jedes weitere Element
// beginnt am Segue (start_next) des vorigen. Block ziehen = Segue des vorigen verschieben.

import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtMs, toast } from "./api";

export const LANE_COLORS: Record<string, string> = { music: "#4f8fd8", jingle: "#e8673c", sweeper: "#d9a23a", top_of_hour: "#c0569e", station_id: "#c0569e", voicetrack: "#8c62d6", ad: "#c8382f", news: "#3d9a8b", promo: "#d9a23a", sponsor: "#d9a23a" };
export const laneColor = (type: string) => LANE_COLORS[type] ?? "#6b8f7f";

/** Symmetric waveform path for peaks (0..1) across [x0, x1], centred at cy with half height hh. */
function wavePath(peaks: number[], x0: number, x1: number, cy: number, hh: number) {
  if (!peaks.length || x1 <= x0) return "";
  const n = peaks.length;
  const step = (x1 - x0) / n;
  let top = `M${x0},${cy}`;
  let bottom = "";
  for (let i = 0; i < n; i++) {
    const x = x0 + i * step;
    const v = Math.min(1, peaks[i]) * hh;
    top += `L${x.toFixed(1)},${(cy - v).toFixed(1)}`;
    bottom = `L${x.toFixed(1)},${(cy + v).toFixed(1)}` + bottom;
  }
  return `${top}L${x1},${cy}${bottom}Z`;
}

export function MiniWave({ peaks, color = "#4f8fd8", width = 160, height = 26, progress }: { peaks: number[] | null | undefined; color?: string; width?: number; height?: number; progress?: number }) {
  if (!peaks?.length) return <svg width={width} height={height} />;
  const d = wavePath(peaks, 0, width, height / 2, height / 2 - 1);
  return (
    <svg width={width} height={height} style={{ display: "block" }}>
      <defs><clipPath id={`p${width}${height}${Math.round((progress ?? 0) * 1000)}`}><rect x={0} y={0} width={(progress ?? 0) * width} height={height} /></clipPath></defs>
      <path d={d} fill={color} opacity={progress != null ? 0.35 : 0.8} />
      {progress != null && <path d={d} fill={color} clipPath={`url(#p${width}${height}${Math.round((progress ?? 0) * 1000)})`} />}
    </svg>
  );
}

/** Big progress waveform of the on-air item with intro/outro marks and remaining time. */
export function OnAirWave({ np }: { np: any }) {
  const id = np?.item?.media_id;
  const [song, setSong] = useState<any>(null);
  const box = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(800);
  useEffect(() => {
    if (!id) return setSong(null);
    api(`/api/media/${id}`).then(setSong).catch(() => setSong(null));
  }, [id]);
  useEffect(() => {
    const ro = new ResizeObserver(() => setW(box.current?.clientWidth ?? 800));
    if (box.current) ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  if (!np) return null;
  const marks = np.item?.marks ?? {};
  const start = marks.cue_in ?? 0;
  const dur = song?.duration_ms || np.len_ms + start || 1;
  const pos = np.pos_ms;
  const end = marks.start_next ?? marks.cue_out ?? dur;
  const remain = Math.max(0, end - pos);
  const x = (ms: number) => (ms / dur) * w;
  const h = 70;
  const intro = marks.intro;
  const introLeft = intro != null ? intro - pos : null;
  return (
    <div className="onair-wave" style={{ display: "flex", gap: 14, alignItems: "center", background: "#14201b", color: "#e6efe9", padding: "8px 14px", borderRadius: 10 }}>
      <div ref={box} style={{ flex: 1, minWidth: 0 }}>
        <div className="small" style={{ opacity: 0.8, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>ON AIR · {np.item?.artist ? `${np.item.artist} – ` : ""}{np.item?.title}</div>
        <svg width={w} height={h} style={{ display: "block" }}>
          {song?.waveform && <path d={wavePath(song.waveform, 0, w, h / 2, h / 2 - 2)} fill="#3b5b4f" />}
          {song?.waveform && (
            <>
              <clipPath id="onair-played"><rect x={0} y={0} width={x(pos)} height={h} /></clipPath>
              <path d={wavePath(song.waveform, 0, w, h / 2, h / 2 - 2)} fill="#e8673c" clipPath="url(#onair-played)" />
            </>
          )}
          {intro != null && <line x1={x(intro)} x2={x(intro)} y1={0} y2={h} stroke="#6fb6ff" strokeWidth={2} />}
          {marks.outro != null && <line x1={x(marks.outro)} x2={x(marks.outro)} y1={0} y2={h} stroke="#c49cff" strokeWidth={2} />}
          <line x1={x(end)} x2={x(end)} y1={0} y2={h} stroke="#ff8a5c" strokeWidth={2} strokeDasharray="4 3" />
          <line x1={x(pos)} x2={x(pos)} y1={0} y2={h} stroke="#fff" strokeWidth={2} />
        </svg>
      </div>
      <div style={{ textAlign: "right", minWidth: 120 }}>
        {introLeft != null && introLeft > 0 && <div className="small" style={{ color: "#6fb6ff" }}>Intro {Math.ceil(introLeft / 1000)} s</div>}
        <div style={{ fontSize: 34, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1 }}>-{fmtMs(remain)}</div>
        <div className="small" style={{ opacity: 0.7 }}>bis zum Nächsten</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- multi-lane editor

export interface LaneRow { id: string; type: string; status: string; label: string; media: any | null; marks: any; media_marks: any; overridden: boolean; vt_stations?: string[]; overlayWarnings?: string[]; overlays?: { media_id?: string; marks?: any; waveform?: number[]; title: string; kind: string; offset_ms: number; length_ms: number; duck_db: number }[] }
export interface Lanes { prev: LaneRow | null; cur: LaneRow; next: LaneRow | null }

const DB_MIN = -40;
const editable = (r: LaneRow | null) => !!r && !["played", "sent"].includes(r.status);
const endOf = (m: any) => m.cue_out ?? m.media_dur ?? 0;
const segOf = (m: any) => m.start_next ?? m.fade_out ?? endOf(m);

/** Gain (dB) of a lane at a file position: level points, else fades. */
function gainAt(m: any, ms: number): number {
  const pts: { ms: number; db: number }[] = m.level_points ?? [];
  if (pts.length) {
    if (ms <= pts[0].ms) return pts[0].db;
    for (let i = 1; i < pts.length; i++) if (ms <= pts[i].ms) return pts[i - 1].db + ((pts[i].db - pts[i - 1].db) * (ms - pts[i - 1].ms)) / Math.max(1, pts[i].ms - pts[i - 1].ms);
    return pts[pts.length - 1].db;
  }
  if (m.fade_in != null && ms < m.fade_in) return DB_MIN + ((0 - DB_MIN) * (ms - (m.cue_in ?? 0))) / Math.max(1, m.fade_in - (m.cue_in ?? 0));
  if (m.fade_out != null && ms > m.fade_out) {
    const fe = m.fade_end ?? endOf(m);
    return Math.max(DB_MIN, (DB_MIN * (ms - m.fade_out)) / Math.max(1, fe - m.fade_out));
  }
  return 0;
}

type Key = "prev" | "cur" | "next";

export function LaneEditor({ lanes, onChange, height = 92, vtSlot, playhead, fine = true }: {
  lanes: Lanes; onChange: (l: Lanes) => void; height?: number; vtSlot?: boolean; playhead?: number | null; fine?: boolean;
}) {
  const [pxs, setPxs] = useState(40); // px per second
  const scroller = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const drag = useRef<null | { kind: "block" | "point" | "trim"; key: Key; idx?: number; x0: number; y0: number; start: any; edge?: "in" | "out" }>(null);
  const [finePeaks, setFinePeaks] = useState<Record<string, { from: number; to: number; peaks: number[] }>>({});
  const [scrollTick, setScrollTick] = useState(0);

  const order: Key[] = ["prev", "cur", "next"];
  const rows = order.map((k) => [k, lanes[k]] as const).filter(([, r]) => r) as [Key, LaneRow][];
  // Timeline start of each lane (ms, where its cue_in plays).
  const starts = useMemo(() => {
    const s: Partial<Record<Key, number>> = {};
    let t = 0;
    let prevRow: LaneRow | null = null;
    for (const [k, r] of rows) {
      if (prevRow) {
        const pm = prevRow.marks;
        t = t + Math.max(0, segOf(pm) - (pm.cue_in ?? 0));
      }
      s[k] = t;
      prevRow = r;
    }
    return s;
  }, [lanes]);
  const lenOf = (r: LaneRow) => (r.media ? Math.max(0, endOf(r.marks) - (r.marks.cue_in ?? 0)) : 4000);
  const total = Math.max(...rows.map(([k, r]) => (starts[k] ?? 0) + lenOf(r))) + 2000;
  const px = pxs / 1000;
  const width = Math.max(600, total * px);
  const laneY = (i: number) => 20 + i * (height + 10);
  const svgH = laneY(rows.length) + 4;
  const dbY = (db: number, y0: number) => y0 + 18 + ((0 - Math.max(DB_MIN, Math.min(6, db))) / (0 - DB_MIN)) * (height - 24);

  // Initial view: around the first transition.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const focus = lanes.prev ? starts.cur ?? 0 : starts.next ?? 0;
    el.scrollLeft = Math.max(0, focus * px - el.clientWidth / 2);
  }, [lanes.cur.id]);

  // Fine peaks for the visible part of each lane (when zoomed in).
  useEffect(() => {
    if (!fine) return;
    const t = window.setTimeout(async () => {
      const el = scroller.current;
      if (!el) return;
      const v0 = el.scrollLeft / px, v1 = (el.scrollLeft + el.clientWidth) / px;
      for (const [k, r] of rows) {
        if (!r.media) continue;
        const ci = r.marks.cue_in ?? 0;
        const from = Math.max(0, Math.round(ci + (v0 - (starts[k] ?? 0))));
        const to = Math.min(r.media.duration_ms, Math.round(ci + (v1 - (starts[k] ?? 0))));
        if (to - from < 1000 || to - from > 110_000) continue;
        const have = finePeaks[r.media.id];
        if (have && have.from <= from && have.to >= to) continue;
        const a = Math.max(0, from - 5000), b = Math.min(r.media.duration_ms, to + 5000, a + 115_000);
        try {
          const res = await api(`/api/media/${r.media.id}/peaks?from=${a}&to=${b}&n=${Math.min(4000, Math.round(((b - a) / 1000) * pxs))}`);
          setFinePeaks((f) => ({ ...f, [r.media.id]: res }));
        } catch { /* keep overview */ }
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [pxs, lanes, fine, scrollTick]);

  const update = (key: Key, patch: any) => {
    const r = lanes[key];
    if (!r) return;
    onChange({ ...lanes, [key]: { ...r, marks: { ...r.marks, ...patch } } });
  };
  const prevKey = (k: Key): Key | null => {
    const i = rows.findIndex(([x]) => x === k);
    return i > 0 ? rows[i - 1][0] : null;
  };

  const onDown = (e: React.PointerEvent, kind: "block" | "point" | "trim", key: Key, idx?: number, edge?: "in" | "out") => {
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const pk = prevKey(key);
    const start = kind === "block" ? (pk ? { seg: segOf(lanes[pk]!.marks) } : null) : kind === "trim" ? { ...lanes[key]!.marks } : { pts: [...(lanes[key]!.marks.level_points ?? [])] };
    drag.current = { kind, key, idx, x0: e.clientX, y0: e.clientY, start, edge };
  };
  const onMove = (e: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dms = (e.clientX - d.x0) / px;
    if (d.kind === "block") {
      const pk = prevKey(d.key);
      if (!pk || !d.start || !editable(lanes[pk])) return;
      const pm = lanes[pk]!.marks;
      const seg = Math.round(Math.max((pm.cue_in ?? 0) + 500, Math.min(endOf(pm), d.start.seg + dms)));
      update(pk, { start_next: seg });
    } else if (d.kind === "trim") {
      const m = d.start;
      if (d.edge === "in") update(d.key, { cue_in: Math.round(Math.max(0, Math.min(endOf(m) - 500, (m.cue_in ?? 0) + dms))) });
      else {
        const out = Math.round(Math.max((m.cue_in ?? 0) + 500, Math.min(lanes[d.key]!.media?.duration_ms ?? endOf(m), endOf(m) + dms)));
        update(d.key, { cue_out: out, start_next: Math.min(segOf(m) + dms, out) });
      }
    } else if (d.kind === "point" && d.idx != null) {
      const pts = [...d.start.pts];
      const p = pts[d.idx];
      const i = rows.findIndex(([x]) => x === d.key);
      const y0 = laneY(i);
      const rect = svg.current!.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const db = Math.round((0 - ((y - y0 - 18) / (height - 24)) * (0 - DB_MIN)) * 2) / 2;
      pts[d.idx] = { ms: Math.max(0, Math.round(p.ms + dms)), db: Math.max(DB_MIN, Math.min(6, db)) };
      update(d.key, { level_points: pts.sort((a: any, b: any) => a.ms - b.ms) });
    }
  };
  const onUp = () => { drag.current = null; };

  /** Double-click on a lane: add an envelope point there (on a point: remove it). */
  const addPoint = (e: React.MouseEvent, key: Key, i: number) => {
    const r = lanes[key];
    if (!r?.media || !editable(r)) return;
    const rect = svg.current!.getBoundingClientRect();
    const t = (e.clientX - rect.left) / px;
    const ms = Math.round((r.marks.cue_in ?? 0) + t - (starts[key] ?? 0));
    const y = e.clientY - rect.top;
    const db = Math.round((0 - ((y - laneY(i) - 18) / (height - 24)) * (0 - DB_MIN)) * 2) / 2;
    const base = r.marks.level_points?.length ? r.marks.level_points : defaultEnvelope(r.marks);
    update(key, { level_points: [...base, { ms, db: Math.max(DB_MIN, Math.min(6, db)) }].sort((a: any, b: any) => a.ms - b.ms) });
  };

  return (
    <div>
      <div className="row" style={{ gap: 6, marginBottom: 6 }}>
        <span className="small" style={{ opacity: 0.75 }}>Zoom</span>
        <button className="btn small" onClick={() => setPxs((z) => Math.max(5, Math.round(z / 1.5)))}>−</button>
        <button className="btn small" onClick={() => setPxs((z) => Math.min(400, Math.round(z * 1.5)))}>+</button>
        <button className="btn small" onClick={() => { const el = scroller.current; if (el) setPxs(Math.max(3, Math.round((el.clientWidth / total) * 1000))); }}>Alles</button>
        <span className="small" style={{ opacity: 0.6 }}>Block ziehen = Einsatz verschieben · Doppelklick = Lautstärkepunkt · Punkt doppelklicken = entfernen{vtSlot ? " · Ränder der Sprachspur ziehen = schneiden" : ""}</span>
      </div>
      <div ref={scroller} style={{ overflowX: "auto", background: "#101915", borderRadius: 10, border: "1px solid #24352e" }} onScroll={() => setScrollTick((n) => n + 1)}>
        <svg ref={svg} width={width} height={svgH} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp} style={{ display: "block", touchAction: "none" }}>
          {/* time axis */}
          {Array.from({ length: Math.ceil(total / 1000) + 1 }, (_, s) => s).filter((s) => s % (pxs < 15 ? 10 : pxs < 40 ? 5 : 1) === 0).map((s) => (
            <g key={s}>
              <line x1={s * 1000 * px} x2={s * 1000 * px} y1={14} y2={svgH} stroke="#1d2b25" />
              <text x={s * 1000 * px + 2} y={11} fontSize={9} fill="#7f978c">{fmtMs(s * 1000)}</text>
            </g>
          ))}
          {rows.map(([k, r], i) => {
            const y0 = laneY(i);
            const x0 = (starts[k] ?? 0) * px;
            const m = r.marks;
            const ci = m.cue_in ?? 0;
            const color = laneColor(r.media?.kind === "voicetrack" ? "voicetrack" : r.type);
            const fx = (ms: number) => x0 + (ms - ci) * px;
            const w = lenOf(r) * px;
            const fp = r.media ? finePeaks[r.media.id] : null;
            const ov = r.media?.waveform ?? [];
            const dur = r.media?.duration_ms || 1;
            // Overview peaks cut to [cue_in, cue_out]; fine peaks drawn over them where loaded.
            const ovFrom = Math.floor((ci / dur) * ov.length), ovTo = Math.ceil((endOf(m) / dur) * ov.length);
            const env = m.level_points?.length ? m.level_points : defaultEnvelope(m);
            const can = editable(r);
            return (
              <g key={r.id}>
                <rect x={x0} y={y0} width={Math.max(w, 2)} height={height} rx={6} fill="#1a2822" stroke={color} strokeOpacity={0.5} />
                <rect x={x0} y={y0} width={Math.max(w, 2)} height={16} rx={6} fill={color} opacity={0.95}
                  style={{ cursor: k !== rows[0][0] && can ? "grab" : "default" }} onPointerDown={(e) => k !== rows[0][0] && onDown(e, "block", k)} />
                <text x={x0 + 6} y={y0 + 12} fontSize={11} fill="#fff" style={{ pointerEvents: "none" }}>
                  {r.media ? `${r.media.artist ? `${r.media.artist} – ` : ""}${r.media.title}` : r.type === "voicetrack" ? "Voicetrack – noch nicht aufgenommen" : r.label || r.type}
                </text>
                {r.media && <path d={wavePath(ov.slice(ovFrom, ovTo), x0, x0 + w, y0 + 18 + (height - 18) / 2, (height - 22) / 2)} fill={color} opacity={fp ? 0.25 : 0.7} style={{ pointerEvents: "none" }} />}
                {fp && <path d={wavePath(fp.peaks, fx(fp.from), fx(fp.to), y0 + 18 + (height - 18) / 2, (height - 22) / 2)} fill={color} opacity={0.75} style={{ pointerEvents: "none" }} />}
                {/* lane surface for double-click */}
                {r.media && <rect x={x0} y={y0 + 16} width={w} height={height - 16} fill="transparent" onDoubleClick={(e) => addPoint(e, k, i)} />}
                {/* cue marks */}
                {r.media && ([["intro", "#6fb6ff"], ["outro", "#c49cff"], ["start_next", "#ff8a5c"]] as const).map(([mk, c]) => m[mk] != null && m[mk] > ci && (
                  <g key={mk} style={{ pointerEvents: "none" }}>
                    <line x1={fx(m[mk])} x2={fx(m[mk])} y1={y0 + 16} y2={y0 + height} stroke={c} strokeWidth={1.5} strokeDasharray={mk === "start_next" ? "4 3" : undefined} />
                    <text x={fx(m[mk]) + 3} y={y0 + height - 4} fontSize={9} fill={c}>{mk === "intro" ? "Intro" : mk === "outro" ? "Post" : "Segue"}</text>
                  </g>
                ))}
                {/* envelope */}
                {r.media && env.length > 0 && (
                  <polyline points={[{ ms: ci, db: gainAt({ ...m, level_points: env }, ci) }, ...env.filter((p: any) => p.ms > ci && p.ms < endOf(m)), { ms: endOf(m), db: gainAt({ ...m, level_points: env }, endOf(m)) }].map((p: any) => `${fx(p.ms)},${dbY(p.db, y0)}`).join(" ")}
                    fill="none" stroke="#ffe08a" strokeWidth={1.5} style={{ pointerEvents: "none" }} />
                )}
                {r.media && can && (m.level_points ?? []).map((p: any, j: number) => (
                  <circle key={j} cx={fx(p.ms)} cy={dbY(p.db, y0)} r={5} fill="#ffe08a" stroke="#101915" style={{ cursor: "move" }}
                    onPointerDown={(e) => onDown(e, "point", k, j)}
                    onDoubleClick={(e) => { e.stopPropagation(); update(k, { level_points: m.level_points.filter((_: any, x: number) => x !== j) }); }}>
                    <title>{`${fmtMs(p.ms)} · ${p.db} dB`}</title>
                  </circle>
                ))}
                {/* elements laid over this one (drop-in on the ramp, imaging, VT over the intro) */}
                {r.media && (r.overlays ?? []).map((o, j) => (
                  <g key={`ov${j}`} style={{ pointerEvents: "none" }}>
                    <rect x={fx(ci + o.offset_ms)} y={y0 + height - 22} width={Math.max(4, o.length_ms * px)} height={18} rx={4} fill="#e8673c" opacity={0.85} />
                    {!!o.waveform?.length && <path d={wavePath(o.waveform, fx(ci + o.offset_ms), fx(ci + o.offset_ms) + Math.max(4, o.length_ms * px), y0 + height - 13, 7)} fill="#ffdc9b" opacity={0.65} />}
                    <text x={fx(ci + o.offset_ms) + 4} y={y0 + height - 9} fontSize={10} fill="#fff">{o.kind === "dropin_ramp" ? "Drop-in" : o.kind === "voicetrack" ? "VT" : "Imaging"}: {o.title}{o.duck_db ? ` (−${o.duck_db} dB)` : ""}</text>
                  </g>
                ))}
                {/* trim handles (voice lane) */}
                {vtSlot && k === "cur" && r.media && can && (
                  <>
                    <rect x={x0 - 3} y={y0 + 16} width={6} height={height - 16} fill="#fff" opacity={0.6} style={{ cursor: "ew-resize" }} onPointerDown={(e) => onDown(e, "trim", k, undefined, "in")} />
                    <rect x={x0 + w - 3} y={y0 + 16} width={6} height={height - 16} fill="#fff" opacity={0.6} style={{ cursor: "ew-resize" }} onPointerDown={(e) => onDown(e, "trim", k, undefined, "out")} />
                  </>
                )}
              </g>
            );
          })}
          {playhead != null && <line x1={playhead * px} x2={playhead * px} y1={0} y2={svgH} stroke="#fff" strokeWidth={2} />}
        </svg>
      </div>
    </div>
  );
}

/** Envelope implied by the fades (so a first double-click starts from what is heard now). */
export function defaultEnvelope(m: any): { ms: number; db: number }[] {
  const pts: { ms: number; db: number }[] = [];
  const ci = m.cue_in ?? 0;
  if (m.fade_in != null && m.fade_in > ci) pts.push({ ms: ci, db: DB_MIN }, { ms: m.fade_in, db: 0 });
  if (m.fade_out != null) pts.push({ ms: m.fade_out, db: 0 }, { ms: m.fade_end ?? endOf(m), db: DB_MIN });
  return pts;
}

/** Timeline starts (ms) for playback, same rule as the editor. */
export function laneStarts(l: Lanes) {
  const out: Partial<Record<Key, number>> = {};
  let t = 0;
  let prev: LaneRow | null = null;
  for (const k of ["prev", "cur", "next"] as Key[]) {
    const r = l[k];
    if (!r) continue;
    if (prev) t += Math.max(0, segOf(prev.marks) - (prev.marks.cue_in ?? 0));
    out[k] = t;
    prev = r;
  }
  return out;
}

/** Preview the lanes from timeline position t0 (ms) with Web Audio (envelopes applied). */
export function usePreview(lanes: Lanes | null) {
  const [playhead, setPlayhead] = useState<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const stopRef = useRef<() => void>(() => {});
  const stop = () => { stopRef.current(); setPlayhead(null); };
  const play = async (t0: number, seconds = 16) => {
    stop();
    if (!lanes) return;
    const ctx = ctxRef.current ?? new AudioContext();
    ctxRef.current = ctx;
    await ctx.resume();
    const starts = laneStarts(lanes);
    const nodes: HTMLAudioElement[] = [];
    const timers: number[] = [];
    const prepared: { a: HTMLAudioElement; gain: GainNode; marks: any; from: number; delay: number; duck: (ms: number) => number }[] = [];
    const prepare = async (id: string, marks: any, laneT: number, duck: (ms: number) => number) => {
      const ci = marks.cue_in ?? 0;
      const from = ci + Math.max(0, t0 - laneT);
      const delay = Math.max(0, laneT - t0);
      if (from >= endOf(marks) || delay > seconds * 1000) return;
      const a = new Audio();
      a.preload = "auto";
      nodes.push(a);
      const loaded = new Promise<boolean>((resolve) => {
        const timer = window.setTimeout(() => resolve(false), 10000);
        a.addEventListener("loadedmetadata", () => { clearTimeout(timer); resolve(true); }, { once: true });
        a.addEventListener("error", () => { clearTimeout(timer); resolve(false); }, { once: true });
      });
      a.src = `/api/media/${id}/audio`;
      if (!await loaded) return;
      const gain = ctx.createGain();
      ctx.createMediaElementSource(a).connect(gain).connect(ctx.destination);
      a.currentTime = from / 1000;
      prepared.push({ a, gain, marks, from, delay, duck });
    };
    const loading: Promise<void>[] = [];
    for (const k of ["prev", "cur", "next"] as Key[]) {
      const r = lanes[k];
      if (!r?.media) continue;
      const laneT = starts[k] ?? 0;
      const ci = r.marks.cue_in ?? 0;
      loading.push(prepare(r.media.id, r.marks, laneT, (ms) => {
        const pos = ms - ci;
        return Math.max(0, ...(r.overlays ?? []).filter((o) => pos >= o.offset_ms && pos < o.offset_ms + o.length_ms).map((o) => o.duck_db ?? 0));
      }));
      for (const o of r.overlays ?? []) {
        if (!o.media_id) continue;
        const marks = { ...o.marks, cue_out: (o.marks?.cue_in ?? 0) + o.length_ms, media_dur: (o.marks?.cue_in ?? 0) + o.length_ms };
        loading.push(prepare(o.media_id, marks, laneT + o.offset_ms, () => 0));
      }
    }
    let cancelled = false;
    const cleanup = () => { cancelled = true; timers.forEach((t) => window.clearTimeout(t)); nodes.forEach((a) => { a.pause(); a.src = ""; }); };
    stopRef.current = cleanup;
    await Promise.all(loading);
    if (cancelled) return;
    const begin = ctx.currentTime + 0.2;
    for (const { a, gain, marks, from, delay, duck } of prepared) {
      const end = Math.min(endOf(marks), from + seconds * 1000 - delay);
      for (let fm = from; fm <= end; fm += 20) {
        gain.gain.setValueAtTime(Math.pow(10, (gainAt(marks, fm) - duck(fm)) / 20), begin + delay / 1000 + (fm - from) / 1000);
      }
      timers.push(window.setTimeout(() => { void a.play().catch(() => {}); }, Math.max(0, (begin - ctx.currentTime) * 1000 + delay)));
      timers.push(window.setTimeout(() => a.pause(), Math.max(0, (begin - ctx.currentTime) * 1000 + delay + end - from)));
    }
    const t0wall = performance.now() + (begin - ctx.currentTime) * 1000;
    let raf = 0;
    const tick = () => {
      const el = performance.now() - t0wall;
      setPlayhead(el < 0 ? t0 : t0 + el);
      if (el > seconds * 1000) return stop();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    stopRef.current = () => {
      cancelAnimationFrame(raf);
      cleanup();
    };
  };
  useEffect(() => () => stopRef.current(), []);
  return { play, stop, playhead };
}

/** Load prev/cur/next of a log row; save only what changed. */
export function useTransition(entryId: string | null) {
  const [orig, setOrig] = useState<Lanes | null>(null);
  const [lanes, setLanesState] = useState<Lanes | null>(null);
  const [history, setHistory] = useState<Lanes[]>([]);
  const load = async () => {
    if (!entryId) return;
    const d = await api(`/api/log/${entryId}/transition`);
    const fix = (r: any) => (r ? { ...r, marks: { ...r.marks, media_dur: r.media?.duration_ms } } : null);
    const l = { prev: fix(d.prev), cur: fix(d.cur), next: fix(d.next) } as Lanes;
    setOrig(l);
    setLanesState(l);
    setHistory([]);
  };
  useEffect(() => { void load(); }, [entryId]);
  const setLanes = (l: Lanes) => { if (lanes) setHistory((h) => [...h.slice(-30), lanes]); setLanesState(l); };
  const undo = () => setHistory((h) => { if (!h.length) return h; setLanesState(h[h.length - 1]); return h.slice(0, -1); });
  const changed = (k: Key) => !!lanes?.[k] && JSON.stringify(lanes[k]!.marks) !== JSON.stringify(orig?.[k]?.marks);
  const dirty = (["prev", "cur", "next"] as Key[]).some(changed);
  const save = async () => {
    if (!lanes) return;
    try {
      for (const k of ["prev", "cur", "next"] as Key[]) {
        const r = lanes[k];
        if (!r || !changed(k) || !editable(r) || !r.media) continue;
        const { media_dur: _d, ...m } = r.marks;
        void _d;
        await api(`/api/log/${r.id}/cues`, { method: "PUT", body: m });
      }
      toast("Übergang gespeichert – gilt nur für diese Ausstrahlung");
      await load();
    } catch (e: any) {
      toast(e.message, true);
    }
  };
  const reset = async () => {
    if (!lanes) return;
    for (const k of ["prev", "cur", "next"] as Key[]) {
      const r = lanes[k];
      if (r && r.overridden && editable(r)) await api(`/api/log/${r.id}/cues`, { method: "PUT", body: { reset: true } });
    }
    toast("Zurückgesetzt auf die Cues der Bibliothek");
    await load();
  };
  return { lanes, setLanes, undo, canUndo: history.length > 0, dirty, save, reset, reload: load };
}
